import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  Plus,
  Upload,
  Loader2,
  CheckCircle2,
  Info,
  Copy,
  ExternalLink,
  X,
} from "lucide-react";
import { eventsApi, refundsApi, fileUrl, isoToLocalInput, localInputToIso, audToCents, centsToAUD, formatAuDate, EVENT_CATEGORIES, tzForState, STATE_TZ_MAP, TZ_FRIENDLY_LABEL } from "../../lib/events";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { extractError } from "../../lib/api";
import AddonsManager from "../../components/AddonsManager";
import SeatingArrangementSection from "../../components/SeatingArrangementSection";
import { PlatformFeesSection } from "../../components/PlatformFeesSection";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

function emptyTicketType(order = 0) {
  return { _localId: `new-${Date.now()}-${order}`, name: "", price_aud: "0", quantity: 50, sort_order: order };
}

export default function EventForm() {
  const { id } = useParams(); // undefined = create
  const isEdit = !!id;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const [event, setEvent] = useState(null); // populated in edit
  // Bubbled up from SeatingArrangementSection so we can toggle the
  // ticket-types-seated-hint without needing the backend to enrich
  // seating_mode on the organiser event endpoint.
  const [derivedSeatingMode, setDerivedSeatingMode] = useState("general_admission");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  // Admin all-organiser edit — override confirmation dialog state.
  // Opens automatically when an admin edits a live event with sales AND has
  // changed a locked field (date/venue/visibility/fee_mode/paid-by).
  const [overrideDialog, setOverrideDialog] = useState(null); // { materialChanges, notifyDefault }
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [notifyBuyers, setNotifyBuyers] = useState(true);
  // Admin all-organiser edit — when eventsApi.get(id) 404s (or 403s), we render
  // an access-denied state instead of a blank editable "Untitled" form.
  const [loadStatus, setLoadStatus] = useState(null); // null | "loaded" | { code, message }

  const [form, setForm] = useState({
    title: "",
    description: "",
    venue_name: "",
    venue_address: "",
    state: "NSW",
    // Sprint AU — event timezone (auto-derived from state unless organiser overrides)
    timezone: "Australia/Sydney",
    timezone_manual: false,
    start_at: "",
    end_at: "",
    capacity: "",
    visibility: "public",
    cover_file_id: null,
    category: "",
    booking_fee_aud: "0.00",
    fee_mode: "absorbed",
    event_terms: "",
    refund_policy: "",
    event_disclaimer: "",
    reminder_enabled: true,
    thankyou_enabled: true,
    thankyou_message: "",
    waitlist_enabled: true,
    // Sprint AK — Token No. settings
    token_numbers_enabled: false,
    ticket_token_label: "Token No.",
    ticket_token_mode: "auto",
    ticket_token_prefix: "",
    // Sprint AN — event paid-by ("" = inherit organiser defaults)
    service_charge_paid_by: "",
    gst_paid_by: "",
    // Sprint AX — external booking (admin-only)
    booking_type: "internal",
    external_booking_url: "",
    external_booking_provider: "",
    // Sprint AO — per-event GST enable (null = inherit)
    gst_enabled: null,
    // Configurable Additional Guest Details — new events default to name required.
    guest_details_config: { name: "required", email: "optional", phone: "optional" },
  });

  const [ticketTypes, setTicketTypes] = useState([emptyTicketType(0)]);

  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    setLoading(true);
    eventsApi
      .get(id)
      .then((e) => {
        if (!alive) return;
        setEvent(e);
        setForm({
          title: e.title || "",
          description: e.description || "",
          venue_name: e.venue_name || "",
          venue_address: e.venue_address || "",
          state: e.state || "NSW",
          // Sprint AU — hydrate persisted timezone; mark as "manual" when it differs from the state default
          timezone: e.timezone || tzForState(e.state),
          timezone_manual: !!e.timezone && e.timezone !== tzForState(e.state),
          start_at: isoToLocalInput(e.start_at),
          end_at: isoToLocalInput(e.end_at),
          capacity: e.capacity || "",
          visibility: e.visibility,
          cover_file_id: e.cover_file_id || null,
          category: e.category || "",
          booking_fee_aud: ((e.booking_fee_cents_per_ticket || 0) / 100).toFixed(2),
          fee_mode: e.fee_mode || "absorbed",
          event_terms: e.event_terms || "",
          refund_policy: e.refund_policy || "",
          event_disclaimer: e.event_disclaimer || "",
          reminder_enabled: e.reminder_enabled !== false,
          // Sprint AX — hydrate external booking fields for admin edit
          booking_type: e.booking_type || "internal",
          external_booking_url: e.external_booking_url || "",
          external_booking_provider: e.external_booking_provider || "",
          thankyou_enabled: e.thankyou_enabled !== false,
          thankyou_message: e.thankyou_message || "",
          waitlist_enabled: e.waitlist_enabled !== false,
          token_numbers_enabled: !!e.token_numbers_enabled,
          ticket_token_label: e.ticket_token_label || "Token No.",
          ticket_token_mode: e.ticket_token_mode || "auto",
          ticket_token_prefix: e.ticket_token_prefix || "",
          // Sprint AN — restore persisted paid-by snapshot
          service_charge_paid_by: e.service_charge_paid_by || "",
          gst_paid_by: e.gst_paid_by || "",
          gst_enabled: e.gst_enabled === false ? false : true,
          // Configurable Additional Guest Details — legacy events resolve to all-off.
          guest_details_config: e.guest_details_config || { name: "off", email: "off", phone: "off" },
        });
        setTicketTypes(
          (e.ticket_types || []).map((t, i) => ({
            id: t.id,
            _localId: t.id,
            name: t.name,
            price_aud: (t.price_cents / 100).toFixed(2),
            quantity: t.quantity,
            sort_order: t.sort_order ?? i,
            sold: t.sold || 0,
            archived: !!t.archived,
          })),
        );
        if (alive) setLoadStatus("loaded");
      })
      .catch((err) => {
        if (!alive) return;
        const code = err?.response?.status || 0;
        // Admin all-organiser edit — render an explicit access-denied surface for 403/404
        // instead of a blank editable form that could be mistaken for a new draft.
        if (code === 404 || code === 403) {
          setLoadStatus({ code, message: code === 404
            ? "This event doesn't exist or you don't have access to it."
            : "You don't have permission to edit this event." });
        } else {
          setError(extractError(err));
        }
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, isEdit]);

  const upd = (k) => (e) => setForm((prev) => {
    const next = { ...prev, [k]: e.target.value };
    // Sprint AU — when state changes and organiser hasn't manually picked a timezone,
    // realign the timezone to the new state's IANA default.
    if (k === "state" && !prev.timezone_manual) {
      next.timezone = tzForState(e.target.value);
    }
    return next;
  });

  const setTimezone = (tz) => setForm((prev) => ({ ...prev, timezone: tz, timezone_manual: tz !== tzForState(prev.state) }));

  const updTT = (idx, k, v) => {
    setTicketTypes((prev) => prev.map((t, i) => (i === idx ? { ...t, [k]: v } : t)));
  };
  const addTT = () => setTicketTypes((prev) => [...prev, emptyTicketType(prev.length)]);
  const removeTT = (idx) => setTicketTypes((prev) => prev.filter((_, i) => i !== idx));

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const res = await eventsApi.uploadCover(file);
      setForm((f) => ({ ...f, cover_file_id: res.id }));
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const buildEventPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    venue_name: form.venue_name.trim(),
    venue_address: form.venue_address.trim(),
    state: form.state,
    // Sprint AU — send the resolved timezone (backend also enforces state-default fallback)
    timezone: form.timezone || tzForState(form.state),
    start_at: localInputToIso(form.start_at),
    end_at: form.end_at ? localInputToIso(form.end_at) : null,
    capacity: form.capacity ? parseInt(form.capacity, 10) : null,
    visibility: form.visibility,
    cover_file_id: form.cover_file_id || null,
    category: form.category || null,
    booking_fee_cents_per_ticket: audToCents(form.booking_fee_aud),
    event_terms: form.event_terms.trim() || null,
    refund_policy: form.refund_policy.trim() || null,
    event_disclaimer: form.event_disclaimer.trim() || null,
    reminder_enabled: form.reminder_enabled,
    thankyou_enabled: form.thankyou_enabled,
    thankyou_message: form.thankyou_message.trim() || null,
    waitlist_enabled: form.waitlist_enabled,
    // Sprint AK — Token No.
    token_numbers_enabled: !!form.token_numbers_enabled,
    ticket_token_label: (form.ticket_token_label || "Token No.").trim(),
    ticket_token_mode: form.ticket_token_mode === "manual" ? "manual" : "auto",
    ticket_token_prefix: (form.ticket_token_prefix || "").trim().toUpperCase() || null,
    // Configurable Additional Guest Details (organiser + admin can set this)
    guest_details_config: form.guest_details_config,
    // Fee configuration (fee_mode / paid-by) + external booking are Admin-only.
    // Organisers never send these; the backend inherits organiser/platform defaults.
    ...(user?.is_admin ? {
      fee_mode: form.fee_mode,
      service_charge_paid_by: form.service_charge_paid_by || null,
      gst_paid_by: form.gst_paid_by || null,
      booking_type: form.booking_type || "internal",
      external_booking_url: (form.external_booking_url || "").trim() || null,
      external_booking_provider: (form.external_booking_provider || "").trim() || null,
    } : {}),
  });

  const buildTicketTypesPayload = () =>
    ticketTypes.map((t, i) => ({
      id: t.id, // present on existing
      name: (t.name || "").trim(),
      price_cents: audToCents(t.price_aud),
      quantity: parseInt(t.quantity || 0, 10) || 0,
      sort_order: i,
    }));

  const validate = () => {
    if (!form.title || form.title.trim().length < 2) return "Event title is required.";
    if (!form.venue_name.trim()) return "Venue name is required.";
    if (!form.venue_address.trim()) return "Venue address is required.";
    if (!form.state) return "State is required.";
    if (!form.start_at) return "Start date & time is required.";
    if (form.end_at && form.end_at <= form.start_at) return "End time must be after start time.";
    const cleanedTT = buildTicketTypesPayload();
    if (cleanedTT.length === 0) return "Add at least one ticket type.";
    for (const [i, t] of cleanedTT.entries()) {
      if (!t.name) return `Ticket type #${i + 1}: name is required.`;
      if (t.quantity < 1) return `Ticket type #${i + 1}: quantity must be ≥ 1.`;
      if (t.price_cents < 0) return `Ticket type #${i + 1}: price cannot be negative.`;
    }
    if (form.capacity) {
      const total = cleanedTT.reduce((s, t) => s + t.quantity, 0);
      if (total > parseInt(form.capacity, 10)) {
        return `Ticket-type quantities (${total}) exceed event capacity (${form.capacity}).`;
      }
    }
    return null;
  };

  const onCreate = async (publishAfter = false) => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setSaving(true);
    try {
      const payload = { ...buildEventPayload(), ticket_types: buildTicketTypesPayload().map(({ id, ...rest }) => rest) };
      const created = await eventsApi.create(payload);
      if (publishAfter) {
        setPublishing(true);
        await eventsApi.publish(created.id);
        setPublishing(false);
        toast.success("Event published");
      } else {
        toast.success("Draft saved");
      }
      navigate(`/dashboard/events/${created.id}/edit`, { replace: true });
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  // Admin all-organiser edit — fields whose change requires an override confirmation
  // + reason when the event already has ticket sales. Buyer notification also
  // defaults to true for the material subset (date/venue/state/address).
  const detectAdminMaterialChanges = () => {
    if (!isEdit || !event || !user?.is_admin) return [];
    const hasAnySold = (event.ticket_types || []).some((t) => (t.sold || 0) > 0);
    if (!hasAnySold) return [];
    const cur = buildEventPayload();
    const changes = [];
    const same = (a, b) => (a ?? "") === (b ?? "");
    // Compare timestamps as instants — the form round-trips ISO strings through
    // browser-local <input type="datetime-local"> which changes the string
    // representation ('2026-08-23T04:53:00+00:00' -> '...T04:53:00.000Z')
    // even when the underlying moment is identical. Falling back to string
    // compare here would fire the override dialog on every unrelated edit.
    const sameInstant = (a, b) => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      const ta = new Date(a).getTime();
      const tb = new Date(b).getTime();
      if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
      return ta === tb;
    };
    if (!sameInstant(cur.start_at, event.start_at)) changes.push({ field: "start_at", label: "Event date/time" });
    if (!sameInstant(cur.end_at || null, event.end_at || null)) changes.push({ field: "end_at", label: "End time" });
    if (!same(cur.venue_name, event.venue_name)) changes.push({ field: "venue_name", label: "Venue name" });
    if (!same(cur.venue_address, event.venue_address)) changes.push({ field: "venue_address", label: "Venue address" });
    if (!same(cur.state, event.state)) changes.push({ field: "state", label: "State" });
    if (!same(cur.visibility, event.visibility)) changes.push({ field: "visibility", label: "Visibility" });
    if (!same(cur.fee_mode, event.fee_mode || "absorbed")) changes.push({ field: "fee_mode", label: "Fee mode" });
    if (!same(cur.service_charge_paid_by, event.service_charge_paid_by)) changes.push({ field: "service_charge_paid_by", label: "Service charge paid-by" });
    if (!same(cur.gst_paid_by, event.gst_paid_by)) changes.push({ field: "gst_paid_by", label: "GST paid-by" });
    return changes;
  };

  const _doUpdate = async ({ reason = null, confirmed = false, notify = false } = {}) => {
    setError("");
    setSaving(true);
    try {
      // 1. Update event scalar fields (with optional admin override metadata)
      const payload = buildEventPayload();
      if (user?.is_admin && confirmed) {
        payload.admin_override_confirmed = true;
        payload.admin_override_reason = reason || null;
        payload.notify_buyers = !!notify;
      }
      await eventsApi.update(id, payload);

      // 2. Reconcile ticket types
      const existingIds = new Set((event.ticket_types || []).map((t) => t.id));
      const currentIds = new Set(ticketTypes.filter((t) => t.id).map((t) => t.id));
      // deletes / archives
      for (const oldTT of event.ticket_types || []) {
        if (!currentIds.has(oldTT.id)) {
          const soldOrReserved = (oldTT.sold || 0) > 0;
          try {
            if (soldOrReserved) {
              // Cannot delete — archive instead so historical orders remain intact.
              await eventsApi.archiveTicketType(id, oldTT.id, reason || undefined);
            } else {
              await eventsApi.removeTicketType(id, oldTT.id);
            }
          } catch (err) { throw err; }
        }
      }
      // updates + creates
      for (let i = 0; i < ticketTypes.length; i++) {
        const t = ticketTypes[i];
        const ttPayload = {
          name: (t.name || "").trim(),
          price_cents: audToCents(t.price_aud),
          quantity: parseInt(t.quantity || 0, 10) || 0,
          sort_order: i,
        };
        if (t.id && existingIds.has(t.id)) {
          // Admin price override on a sold ticket type — go through the admin endpoint
          // which bypasses the "no price change after sales" lock and requires a reason.
          const originalTT = (event.ticket_types || []).find((x) => x.id === t.id);
          if (
            user?.is_admin &&
            confirmed &&
            originalTT &&
            (originalTT.sold || 0) > 0 &&
            ttPayload.price_cents !== originalTT.price_cents
          ) {
            await eventsApi.adminUpdateTicketPrice(id, t.id, ttPayload.price_cents, reason || undefined);
            // send everything except price via the normal update
            const rest = { ...ttPayload };
            delete rest.price_cents;
            await eventsApi.updateTicketType(id, t.id, rest);
          } else {
            await eventsApi.updateTicketType(id, t.id, ttPayload);
          }
        } else {
          await eventsApi.addTicketType(id, ttPayload);
        }
      }

      // 3. Reload
      const fresh = await eventsApi.get(id);
      setEvent(fresh);
      setTicketTypes(
        (fresh.ticket_types || []).map((t, i) => ({
          id: t.id,
          _localId: t.id,
          name: t.name,
          price_aud: (t.price_cents / 100).toFixed(2),
          quantity: t.quantity,
          sort_order: t.sort_order ?? i,
          sold: t.sold || 0,
          archived: !!t.archived,
        })),
      );
      toast.success(notify ? "Changes saved · buyers notified" : "Changes saved");
    } catch (err) {
      setError(extractError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const onUpdate = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    // Admin all-organiser edit — intercept post-sales edits behind an override dialog.
    const materialChanges = detectAdminMaterialChanges();
    if (materialChanges.length > 0) {
      const notifyFields = new Set(["start_at", "end_at", "venue_name", "venue_address", "state"]);
      const notifyDefault = materialChanges.some((c) => notifyFields.has(c.field));
      setOverrideReason("");
      setOverrideConfirmed(false);
      setNotifyBuyers(notifyDefault);
      setOverrideDialog({ materialChanges, notifyDefault });
      return;
    }
    try { await _doUpdate(); } catch (_) { /* toast via setError already */ }
  };

  const confirmOverrideAndSave = async () => {
    if (!overrideConfirmed) {
      toast.error("Please tick the confirmation checkbox to proceed.");
      return;
    }
    if (!overrideReason.trim()) {
      toast.error("Please provide a reason for this override.");
      return;
    }
    if (!notifyBuyers) {
      const proceed = window.confirm(
        "Buyers will NOT be automatically notified of these changes. Continue?"
      );
      if (!proceed) return;
    }
    try {
      await _doUpdate({ reason: overrideReason.trim(), confirmed: true, notify: notifyBuyers });
      setOverrideDialog(null);
    } catch (_) {
      // stay on dialog so admin can retry / correct
    }
  };

  const onPublish = async () => {
    setPublishing(true);
    try {
      const fresh = await eventsApi.publish(id);
      setEvent(fresh);
      toast.success("Event published");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPublishing(false);
    }
  };

  const onUnpublish = async () => {
    setPublishing(true);
    try {
      const fresh = await eventsApi.unpublish(id);
      setEvent(fresh);
      toast.success("Event moved back to draft");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPublishing(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this draft event? This cannot be undone.")) return;
    try {
      await eventsApi.remove(id);
      toast.success("Draft deleted");
      navigate("/dashboard/events");
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const onCancelEvent = async () => {
    const reason = window.prompt(
      "Cancel this event and issue bulk refunds?\n\nEvery paid order will be refunded via Stripe and every ticket voided. This cannot be undone.\n\nOptional reason (shown to buyers):",
      "",
    );
    if (reason === null) return; // user cancelled
    setPublishing(true);
    try {
      const r = await refundsApi.cancelEvent(id, { reason: reason.trim() || undefined });
      toast.success(`Event cancelled — ${r.orders_processed} order(s) processed.`);
      // reload event
      const ev = await eventsApi.get(id);
      setEvent(ev);
    } catch (err) {
      toast.error(extractError(err));
    } finally { setPublishing(false); }
  };

  const publicHref =
    event && event.status === "published"
      ? event.visibility === "private" && event.private_url_key
        ? `/e/private/${event.private_url_key}`
        : `/e/${event.slug}`
      : null;

  const isPublished = event?.status === "published";
  const hasSold = (event?.ticket_types || []).some((t) => (t.sold || 0) > 0);

  if (loading) {
    return (
      <section className="p-10 flex items-center gap-3" data-testid="event-form-loading">
        <Loader2 className="animate-spin" size={16} />
        <span className="text-sm text-[var(--gt-ink-2)]">Loading event…</span>
      </section>
    );
  }

  // Admin all-organiser edit — access-denied surface for 403/404 responses on load.
  if (loadStatus && typeof loadStatus === "object" && loadStatus.code) {
    return (
      <section className="p-6 sm:p-10" data-testid="event-form-not-found">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="uppercase tracking-widest text-xs text-[var(--gt-ink-3)] mb-3">
            {loadStatus.code === 403 ? "Access denied" : "Not found"}
          </div>
          <h1 className="text-4xl font-black tracking-tight mb-3">This event isn&apos;t editable here.</h1>
          <p className="text-[var(--gt-ink-2)] mb-8">{loadStatus.message}</p>
          <Link to="/dashboard/events" className="gt-btn-primary">
            <ArrowLeft size={14} /> Back to my events
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="p-6 sm:p-10" data-testid="event-form-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link to="/dashboard/events" className="gt-btn-ghost -ml-4 mb-2 text-sm" data-testid="back-to-events">
              <ArrowLeft size={14} /> Back to events
            </Link>
            <div className="gt-label mb-2">
              {isEdit ? (isPublished ? "Edit published event" : "Edit draft") : "Create event"}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter" data-testid="event-form-title">
              {isEdit ? event?.title || "Untitled" : "New event"}
            </h2>
          </div>

          {isEdit && (
            <div className="flex flex-wrap items-center gap-2">
              {isEdit && isPublished && (
                <>
                  <Link
                    to={`/dashboard/events/${event.id}/reports`}
                    className="gt-btn-secondary text-sm py-2"
                    data-testid="view-reports-btn"
                  >
                    Reports
                  </Link>
                  <Link
                    to={`/dashboard/events/${event.id}/scanners`}
                    className="gt-btn-secondary text-sm py-2"
                    data-testid="manage-scanners-btn"
                  >
                    Scanners
                  </Link>
                </>
              )}
              {isPublished && publicHref && (
                <a
                  href={publicHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gt-btn-secondary text-sm py-2"
                  data-testid="view-public-btn"
                >
                  View public <ExternalLink size={14} />
                </a>
              )}
              {isPublished ? (
                <button
                  onClick={onUnpublish}
                  className="gt-btn-secondary text-sm py-2"
                  disabled={publishing || hasSold}
                  title={hasSold ? "Cannot unpublish once tickets have been sold" : ""}
                  data-testid="unpublish-btn"
                >
                  {publishing ? <Loader2 size={14} className="animate-spin" /> : "Unpublish"}
                </button>
              ) : (
                <button
                  onClick={onPublish}
                  className="gt-btn-primary text-sm py-2"
                  disabled={publishing || saving}
                  data-testid="publish-btn"
                >
                  {publishing ? <Loader2 size={14} className="animate-spin" /> : "Publish"}
                </button>
              )}
              {isPublished && event?.status !== "cancelled" && (
                <button
                  onClick={onCancelEvent}
                  className="gt-btn-ghost text-sm py-2 text-[var(--gt-accent)] hover:text-[var(--gt-accent-hover)]"
                  disabled={publishing}
                  data-testid="cancel-event-btn"
                  title="Cancel event and issue bulk refunds"
                >
                  {publishing ? <Loader2 size={14} className="animate-spin" /> : "Cancel event"}
                </button>
              )}
              {!isPublished && (
                <button
                  onClick={onDelete}
                  className="gt-btn-ghost text-sm py-2 text-[var(--gt-accent)] hover:text-[var(--gt-accent-hover)]"
                  data-testid="delete-btn"
                >
                  <Trash2 size={14} /> Delete draft
                </button>
              )}
            </div>
          )}
        </div>

        {isEdit && isPublished && event?.visibility === "private" && event?.private_url_key && (
          <div className="gt-card mb-6 flex items-start gap-3" data-testid="private-url-banner">
            <Info size={18} className="text-[var(--gt-accent)] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="gt-label mb-1">Private event URL</div>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="font-mono text-xs bg-[var(--gt-bg-surface)] px-2 py-1 rounded-sm break-all" data-testid="private-url-code">
                  {typeof window !== "undefined" ? window.location.origin : ""}/e/private/{event.private_url_key}
                </code>
                <button
                  className="gt-btn-ghost text-xs !py-1"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/e/private/${event.private_url_key}`);
                    toast.success("Private URL copied");
                  }}
                  data-testid="copy-private-url-btn"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              <p className="text-xs text-[var(--gt-ink-2)] mt-2">
                This event is not listed publicly. Share this URL with your invitees only.
              </p>
            </div>
          </div>
        )}

        {isEdit && event?.created_from_event_id && event?.status === "draft" && (
          <div className="border border-amber-500 bg-amber-50 rounded-sm p-4 mb-6" data-testid="second-show-banner">
            <div className="text-sm font-semibold">
              Second show of {event.created_from_event_title || "the original event"} — confirm the date before publishing.
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {[["Next day, same time", 1], ["Next week, same time", 7]].map(([label, days]) => (
                <button
                  key={days}
                  type="button"
                  className="border border-amber-500 bg-white rounded-sm px-2.5 py-1.5 font-semibold hover:bg-amber-100"
                  data-testid={`second-show-plus-${days}d-btn`}
                  onClick={() => {
                    const base = event.created_from_start_at ? new Date(event.created_from_start_at) : new Date();
                    const d = new Date(base.getTime() + days * 86400000);
                    const dur = event.created_from_start_at && event.end_at && form.end_at
                      ? new Date(localInputToIso(form.end_at)).getTime() - new Date(localInputToIso(form.start_at)).getTime()
                      : null;
                    setForm((prev) => ({
                      ...prev,
                      start_at: isoToLocalInput(d.toISOString()),
                      end_at: dur ? isoToLocalInput(new Date(d.getTime() + dur).toISOString()) : prev.end_at,
                    }));
                    toast.success(`Start date set to ${label.toLowerCase()}`);
                  }}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                className="border border-amber-500 bg-white rounded-sm px-2.5 py-1.5 font-semibold hover:bg-amber-100"
                data-testid="second-show-custom-btn"
                onClick={() => document.querySelector('input[type="datetime-local"]')?.focus()}
              >
                Custom date/time
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="space-y-8" data-testid="event-form">
          {/* Sprint AL/AM — Admin editing banner (only when admin is editing another organiser's event) */}
          {isEdit && event && user?.is_admin && event.organiser_id && event.organiser_id !== user.id && (
            <div
              className="gt-card border-2 border-[var(--gt-accent)] bg-[var(--gt-accent-tint,#fff4ef)]"
              data-testid="admin-edit-banner"
            >
              <div className="gt-label mb-1 text-[var(--gt-accent)]">Admin editing</div>
              <div className="text-sm">
                You are editing an event owned by another organiser. All changes are audit-logged.
              </div>
            </div>
          )}
          {/* Section 1: Basics */}
          <div className="gt-card">
            <div className="gt-label mb-4">01 · Basics</div>
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="gt-form-label">Event title</label>
                <input
                  className="gt-input"
                  value={form.title}
                  onChange={upd("title")}
                  placeholder="Sydney Harbour Jazz Night"
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <label className="gt-form-label">Description <span className="text-[var(--gt-ink-3)] font-normal">(optional)</span></label>
                <textarea
                  className="gt-input min-h-[100px]"
                  value={form.description}
                  onChange={upd("description")}
                  placeholder="An evening of live jazz on the harbour foreshore…"
                  data-testid="event-description-input"
                />
              </div>
              <div>
                <label className="gt-form-label">Category <span className="text-[var(--gt-ink-3)] font-normal">(optional — helps buyers find your event)</span></label>
                <select
                  className="gt-input"
                  value={form.category}
                  onChange={upd("category")}
                  data-testid="event-category-select"
                >
                  <option value="">— No category —</option>
                  {EVENT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="gt-form-label">Start (local time)</label>
                  <input
                    className="gt-input"
                    type="datetime-local"
                    value={form.start_at}
                    onChange={upd("start_at")}
                    data-testid="event-start-input"
                  />
                </div>
                <div>
                  <label className="gt-form-label">End <span className="text-[var(--gt-ink-3)] font-normal">(optional)</span></label>
                  <input
                    className="gt-input"
                    type="datetime-local"
                    value={form.end_at}
                    onChange={upd("end_at")}
                    data-testid="event-end-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Venue */}
          <div className="gt-card">
            <div className="gt-label mb-4">02 · Venue</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="gt-form-label">Venue name</label>
                <input
                  className="gt-input"
                  value={form.venue_name}
                  onChange={upd("venue_name")}
                  placeholder="Opera House Forecourt"
                  data-testid="event-venue-name-input"
                />
              </div>
              <div>
                <label className="gt-form-label">State</label>
                <select
                  className="gt-input"
                  value={form.state}
                  onChange={upd("state")}
                  data-testid="event-state-select"
                >
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="gt-form-label">Venue address</label>
                <input
                  className="gt-input"
                  value={form.venue_address}
                  onChange={upd("venue_address")}
                  placeholder="Bennelong Point, Sydney NSW 2000"
                  data-testid="event-venue-address-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="gt-form-label flex items-center justify-between gap-2">
                  <span>Event timezone</span>
                  {!form.timezone_manual && (
                    <span className="text-[10px] font-normal normal-case tracking-normal text-[var(--gt-ink-3)]" data-testid="event-timezone-auto-hint">
                      Auto-detected from {form.state}
                    </span>
                  )}
                  {form.timezone_manual && (
                    <button
                      type="button"
                      onClick={() => setTimezone(tzForState(form.state))}
                      className="text-[10px] font-normal normal-case tracking-normal underline text-[var(--gt-accent)]"
                      data-testid="event-timezone-reset-btn"
                    >
                      Reset to {form.state} default
                    </button>
                  )}
                </label>
                <select
                  className="gt-input"
                  value={form.timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  data-testid="event-timezone-select"
                >
                  {Object.entries(TZ_FRIENDLY_LABEL).map(([tz, label]) => (
                    <option key={tz} value={tz}>{tz} — {label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">
                  Buyers see event times in this timezone on the public page, checkout, tickets and emails.
                </p>
              </div>
            </div>
          </div>

          {/* Section 02b: Seating arrangement (seating-hotfix) */}
          <SeatingArrangementSection
            eventId={isEdit ? id : null}
            event={event}
            onSeatingModeChange={setDerivedSeatingMode}
            onChanged={async () => {
              if (!isEdit) return;
              try {
                const fresh = await eventsApi.get(id);
                setEvent(fresh);
              } catch {
                /* ignore — the section will re-fetch on next render anyway */
              }
            }}
          />

          {/* Section 3: Ticket Types */}
          <div className="gt-card">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="gt-label">03 · Ticket types</div>
                <p className="text-xs text-[var(--gt-ink-2)] mt-1">Prices in AUD. Use 0.00 for free tickets.</p>
              </div>
              <button type="button" onClick={addTT} className="gt-btn-secondary text-sm py-2" data-testid="add-ticket-type-btn">
                <Plus size={14} /> Add type
              </button>
            </div>
            {derivedSeatingMode && derivedSeatingMode !== "general_admission" && (
              <div
                className="mb-4 flex items-start gap-3 p-3 rounded-sm bg-[var(--gt-bg-surface)] border border-[var(--gt-line)]"
                data-testid="ticket-types-seated-hint"
              >
                <Info size={14} className="text-[var(--gt-ink-2)] shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--gt-ink-2)]">
                  Reserved seating capacity comes from the seating layout.
                  Ticket / pricing tiers here should match your seating sections
                  or pricing zones.
                </p>
              </div>
            )}
            <div className="flex flex-col gap-3" data-testid="ticket-types-list">
              {ticketTypes.map((t, i) => (
                <div
                  key={t._localId}
                  className="grid grid-cols-12 gap-3 items-end p-4 border border-[var(--gt-line)] rounded-sm bg-[var(--gt-bg-marketing)]"
                  data-testid={`ticket-type-row-${i}`}
                >
                  <div className="col-span-12 sm:col-span-5">
                    <label className="gt-form-label">Name</label>
                    <input
                      className="gt-input"
                      value={t.name}
                      onChange={(e) => updTT(i, "name", e.target.value)}
                      placeholder="Adult · VIP · Child…"
                      data-testid={`tt-name-${i}`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label className="gt-form-label">Price (AUD)</label>
                    <input
                      className="gt-input font-mono"
                      value={t.price_aud}
                      onChange={(e) => updTT(i, "price_aud", e.target.value)}
                      inputMode="decimal"
                      placeholder="0.00"
                      data-testid={`tt-price-${i}`}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-3">
                    <label className="gt-form-label">Quantity</label>
                    <input
                      className="gt-input font-mono"
                      value={t.quantity}
                      onChange={(e) => updTT(i, "quantity", e.target.value)}
                      inputMode="numeric"
                      placeholder="100"
                      data-testid={`tt-qty-${i}`}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex sm:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if ((t.sold && t.sold > 0) && user?.is_admin) {
                          const ok = window.confirm(
                            "This ticket type has sold tickets. It will be archived (hidden from new sales) instead of deleted, preserving all historical orders. Continue?"
                          );
                          if (!ok) return;
                        }
                        removeTT(i);
                      }}
                      className="gt-btn-ghost !p-2"
                      disabled={
                        ticketTypes.length === 1 ||
                        (!!(t.sold && t.sold > 0) && !user?.is_admin)
                      }
                      title={
                        (t.sold && t.sold > 0)
                          ? (user?.is_admin ? "Archive (admin) — hides from new sales, preserves history" : "Cannot remove — has sales")
                          : "Remove"
                      }
                      data-testid={`tt-remove-${i}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {t.sold > 0 && (
                    <div className="col-span-12 text-xs text-[var(--gt-ink-2)]" data-testid={`tt-sold-hint-${i}`}>
                      {t.sold} sold · Price locked{user?.is_admin ? " (admin can override with reason)" : ""}, quantity can only be increased.
                    </div>
                  )}
                  {t.archived && (
                    <div className="col-span-12 flex items-center gap-2 text-xs" data-testid={`tt-archived-hint-${i}`}>
                      <span className="uppercase tracking-widest text-[10px] font-bold px-2 py-0.5 border border-[var(--gt-ink)]">Archived</span>
                      <span className="text-[var(--gt-ink-2)]">Hidden from new sales · historical orders preserved.</span>
                      {user?.is_admin && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await eventsApi.unarchiveTicketType(id, t.id);
                              const fresh = await eventsApi.get(id);
                              setEvent(fresh);
                              setTicketTypes(
                                (fresh.ticket_types || []).map((tt, i2) => ({
                                  id: tt.id, _localId: tt.id, name: tt.name,
                                  price_aud: (tt.price_cents / 100).toFixed(2),
                                  quantity: tt.quantity, sort_order: tt.sort_order ?? i2,
                                  sold: tt.sold || 0, archived: !!tt.archived,
                                })),
                              );
                              toast.success("Ticket type unarchived");
                            } catch (err) { toast.error(extractError(err)); }
                          }}
                          className="text-[var(--gt-accent)] font-semibold hover:underline"
                          data-testid={`tt-unarchive-${i}`}
                        >
                          Unarchive
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 border-t border-[var(--gt-line)] pt-6">
              <div>
                <label className="gt-form-label">Overall event capacity <span className="text-[var(--gt-ink-3)] font-normal">(optional)</span></label>
                <input
                  className="gt-input font-mono"
                  value={form.capacity}
                  onChange={upd("capacity")}
                  inputMode="numeric"
                  placeholder="Leave blank to use the sum of ticket quantities"
                  data-testid="event-capacity-input"
                />
              </div>
              <div>
                <label className="gt-form-label">Visibility</label>
                <div className="flex gap-2">
                  {["public", "private"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm({ ...form, visibility: v })}
                      className={`flex-1 border rounded-sm px-4 py-3 text-sm font-semibold transition-colors ${
                        form.visibility === v
                          ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
                          : "border-[var(--gt-line)] bg-white text-[var(--gt-ink-2)] hover:border-[var(--gt-ink-2)]"
                      }`}
                      disabled={hasSold && isPublished && !user?.is_admin}
                      data-testid={`visibility-${v}`}
                    >
                      {v === "public" ? "Public" : "Private (link-only)"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sprint AX — Ticket Booking Method (Admin only) */}
            {user?.is_admin && (
              <div className="mt-6 border-t border-[var(--gt-line)] pt-6" data-testid="admin-booking-type-section">
                <label className="gt-form-label">Ticket Booking Method</label>
                <p className="text-xs text-[var(--gt-ink-2)] mb-3">
                  Admin-only. Choose whether ticket sales happen on Getickets or on an external ticketing website.
                </p>
                <div className="flex gap-2 max-w-xl">
                  {[
                    { value: "internal", label: "Use Getickets Booking", hint: "Customers buy through Getickets checkout." },
                    { value: "external", label: "External Booking Website", hint: "Customers are redirected to another site." },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        booking_type: opt.value,
                        ...(opt.value === "internal" ? { external_booking_url: "", external_booking_provider: "" } : {}),
                      })}
                      className={`flex-1 text-left border rounded-sm px-4 py-3 text-sm font-semibold transition-colors ${
                        (form.booking_type || "internal") === opt.value
                          ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
                          : "border-[var(--gt-line)] bg-white text-[var(--gt-ink-2)] hover:border-[var(--gt-ink-2)]"
                      }`}
                      data-testid={`booking-type-${opt.value}`}
                    >
                      <div>{opt.label}</div>
                      <div className={`text-[10px] font-normal mt-1 ${(form.booking_type || "internal") === opt.value ? "text-white/70" : "text-[var(--gt-ink-3)]"}`}>
                        {opt.hint}
                      </div>
                    </button>
                  ))}
                </div>
                {form.booking_type === "external" && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="gt-form-label">External Booking URL <span className="text-[var(--gt-accent)]">*</span></label>
                      <input
                        type="url"
                        className="gt-input font-mono"
                        placeholder="https://www.ticketmaster.com.au/event/..."
                        value={form.external_booking_url || ""}
                        onChange={(e) => setForm({ ...form, external_booking_url: e.target.value })}
                        data-testid="external-booking-url-input"
                      />
                      <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">Must start with https:// or http://.</p>
                    </div>
                    <div>
                      <label className="gt-form-label">External Booking Provider (optional)</label>
                      <input
                        type="text"
                        list="external-provider-suggestions"
                        className="gt-input"
                        placeholder="Ticketmaster, TryBooking, Eventbrite, Humanitix, Other"
                        value={form.external_booking_provider || ""}
                        onChange={(e) => setForm({ ...form, external_booking_provider: e.target.value })}
                        data-testid="external-booking-provider-input"
                      />
                      <datalist id="external-provider-suggestions">
                        <option value="Ticketmaster" />
                        <option value="TryBooking" />
                        <option value="Eventbrite" />
                        <option value="Humanitix" />
                        <option value="Other" />
                      </datalist>
                      <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">
                        Shown to buyers in the Get Tickets panel copy. Getickets checkout, service fee and GST are skipped for external events.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}


            <div className="mt-6 border-t border-[var(--gt-line)] pt-6">
              <label className="gt-form-label">Pre-event reminder email</label>
              <p className="text-xs text-[var(--gt-ink-2)] mb-3">
                Automatically email ticket holders 24 hours before the event starts, with their tickets attached.
              </p>
              <div className="flex gap-2 max-w-xs">
                {[true, false].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    onClick={() => setForm({ ...form, reminder_enabled: v })}
                    className={`flex-1 border rounded-sm px-4 py-3 text-sm font-semibold transition-colors ${
                      form.reminder_enabled === v
                        ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
                        : "border-[var(--gt-line)] bg-white text-[var(--gt-ink-2)] hover:border-[var(--gt-ink-2)]"
                    }`}
                    data-testid={v ? "reminder-toggle-on" : "reminder-toggle-off"}
                  >
                    {v ? "On (24h before)" : "Off"}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 border-t border-[var(--gt-line)] pt-6">
              <label className="gt-form-label">Post-event thank-you email</label>
              <p className="text-xs text-[var(--gt-ink-2)] mb-3">
                Automatically thank attendees ~12 hours after the event ends, with a feedback link and your upcoming events. Buyers can unsubscribe from follow-up emails.
              </p>
              <div className="flex gap-2 max-w-xs">
                {[true, false].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    onClick={() => setForm({ ...form, thankyou_enabled: v })}
                    className={`flex-1 border rounded-sm px-4 py-3 text-sm font-semibold transition-colors ${
                      form.thankyou_enabled === v
                        ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
                        : "border-[var(--gt-line)] bg-white text-[var(--gt-ink-2)] hover:border-[var(--gt-ink-2)]"
                    }`}
                    data-testid={v ? "thankyou-toggle-on" : "thankyou-toggle-off"}
                  >
                    {v ? "On (12h after)" : "Off"}
                  </button>
                ))}
              </div>
              {form.thankyou_enabled && (
                <div className="mt-4">
                  <label className="gt-form-label">Personal message <span className="text-[var(--gt-ink-3)] font-normal">(optional)</span></label>
                  <textarea
                    className="gt-input min-h-[80px]"
                    maxLength={2000}
                    value={form.thankyou_message}
                    onChange={upd("thankyou_message")}
                    placeholder="e.g. Thanks so much for coming — see you at our next show!"
                    data-testid="thankyou-message-input"
                  />
                  <div className="text-right text-[10px] text-[var(--gt-ink-3)] mt-1">{form.thankyou_message.length}/2,000</div>
                </div>
              )}
            </div>

            {isEdit && event?.slug && (
              <div className="mt-6 border-t border-[var(--gt-line)] pt-6" data-testid="social-preview-panel">
                <label className="gt-form-label">Social preview</label>
                <p className="text-xs text-[var(--gt-ink-2)] mb-3">
                  This card appears when your share link is posted on Facebook, WhatsApp, X, LinkedIn and more.
                  It refreshes automatically when you save changes.
                  {!(event.status === "published" && event.visibility === "public") && (
                    <span className="block mt-1 text-[var(--gt-accent)] font-medium">
                      Shows generic Getickets branding until this event is published &amp; public.
                    </span>
                  )}
                </p>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/og/e/${event.slug}.png?v=${encodeURIComponent(event.updated_at || "")}`}
                  alt="Social share preview card"
                  className="w-full max-w-md border border-[var(--gt-line)] rounded-sm"
                  data-testid="social-preview-image"
                />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <code className="text-[11px] bg-[var(--gt-bg-surface)] border border-[var(--gt-line)] rounded-sm px-2 py-1.5 break-all">
                    {`${process.env.REACT_APP_BACKEND_URL}/api/s/${event.slug}`}
                  </code>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/s/${event.slug}`);
                        toast.success("Share link copied");
                      } catch {
                        toast.error("Copy failed");
                      }
                    }}
                    className="text-xs font-semibold px-3 py-1.5 border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)]"
                    data-testid="social-preview-copy-btn"
                  >
                    Copy share link
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Section 3b: Ticket Token Numbers (Sprint AK) */}
          <div className="gt-card" data-testid="event-token-section">
            <div className="gt-label mb-4">03b · Ticket token numbers</div>
            <p className="text-xs text-[var(--gt-ink-2)] mb-5">
              Optional operational number printed on each ticket — useful for food/meal
              collection, queue control or merchandise pickup. Display-only; QR remains the
              real admission control.
            </p>
            <label className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                checked={!!form.token_numbers_enabled}
                onChange={(e) => setForm({ ...form, token_numbers_enabled: e.target.checked })}
                className="mt-1"
                data-testid="event-token-enabled"
              />
              <span>
                <div className="text-sm font-semibold text-[var(--gt-ink)]">Enable ticket token numbers</div>
                <div className="text-xs text-[var(--gt-ink-2)]">When off, tickets carry no token and the buyer flow is unchanged.</div>
              </span>
            </label>
            {form.token_numbers_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="gt-form-label">Label</label>
                  <input
                    className="gt-input"
                    value={form.ticket_token_label}
                    onChange={(e) => setForm({ ...form, ticket_token_label: e.target.value.slice(0, 32) })}
                    placeholder="Token No."
                    maxLength={32}
                    data-testid="event-token-label"
                  />
                </div>
                <div>
                  <label className="gt-form-label">Assignment</label>
                  <select
                    className="gt-input"
                    value={form.ticket_token_mode}
                    onChange={(e) => setForm({ ...form, ticket_token_mode: e.target.value })}
                    data-testid="event-token-mode"
                  >
                    <option value="auto">Auto (assign on purchase)</option>
                    <option value="manual">Manual (I&apos;ll assign later)</option>
                  </select>
                </div>
                <div>
                  <label className="gt-form-label">Prefix (optional)</label>
                  <input
                    className="gt-input"
                    value={form.ticket_token_prefix}
                    onChange={(e) => setForm({ ...form, ticket_token_prefix: e.target.value.toUpperCase().slice(0, 12) })}
                    placeholder="e.g. FOOD"
                    maxLength={12}
                    data-testid="event-token-prefix"
                  />
                  <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">A–Z, 0–9, dash · max 12</p>
                </div>
              </div>
            )}
          </div>

          {/* Section 3c: Guest & attendee details (Configurable Additional Guest Details) */}
          <div className="gt-card" data-testid="event-guest-details-section">
            <div className="gt-label mb-1">03c · Guest &amp; attendee details</div>
            <p className="text-xs text-[var(--gt-ink-2)] mb-5">
              Collect details for each individual ticket holder at checkout — separate from the
              buyer&apos;s contact details. Choose whether each field is off, optional or required.
              Changes apply to <span className="font-semibold">new bookings only</span>; existing
              tickets are never changed.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: "name", label: "Guest name", tid: "guest-config-name" },
                { key: "email", label: "Guest email", tid: "guest-config-email" },
                { key: "phone", label: "Guest mobile", tid: "guest-config-phone" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="gt-form-label">{f.label}</label>
                  <select
                    className="gt-input"
                    value={form.guest_details_config?.[f.key] || "off"}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        guest_details_config: { ...prev.guest_details_config, [f.key]: e.target.value },
                      }))
                    }
                    data-testid={f.tid}
                  >
                    <option value="off">Off — don&apos;t collect</option>
                    <option value="optional">Optional</option>
                    <option value="required">Required</option>
                  </select>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[var(--gt-ink-3)] mt-3">
              With all three off, checkout only collects the buyer&apos;s details (today&apos;s behaviour).
            </p>
          </div>

          {/* Section 4: Fees & GST (Sprint AT — Admin-only; hidden from organisers per option c) */}
          {user?.is_admin && (
            <PlatformFeesSection
              scPaidBy={form.service_charge_paid_by}
              onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
              locked={isEdit && !!event?.payment_settings_locked}
              isAdmin={!!user?.is_admin}
              eventFeeConfig={isEdit ? event : null}
            />
          )}

          {/* Section 5: Banner */}
          <div className="gt-card">
            <div className="gt-label mb-4">05 · Banner image</div>
            <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-6 items-start">
              <div className="aspect-[16/9] bg-[var(--gt-bg-surface)] border border-[var(--gt-line)] rounded-sm overflow-hidden relative">
                {form.cover_file_id ? (
                  <img src={fileUrl(form.cover_file_id)} alt="Banner" className="absolute inset-0 w-full h-full object-cover" data-testid="cover-preview" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--gt-ink-3)] text-xs">
                    No banner yet
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-[var(--gt-ink-2)] mb-4">
                  Upload a 16:9 image (JPEG/PNG/WEBP, up to 5 MB). It appears on your public event page.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="cover-file-input"
                />
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="gt-btn-secondary text-sm py-2"
                    disabled={uploading}
                    data-testid="cover-upload-btn"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {form.cover_file_id ? "Replace image" : "Upload image"}
                  </button>
                  {form.cover_file_id && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cover_file_id: null })}
                      className="gt-btn-ghost text-sm py-2"
                      data-testid="cover-remove-btn"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 6: Policies & Terms (Sprint S) */}
          <div className="gt-card" data-testid="event-policies-section">
            <div className="gt-label mb-1">06 · Policies &amp; terms</div>
            <p className="text-xs text-[var(--gt-ink-2)] mb-5">
              Shown to buyers on your public event page as collapsible sections. All optional.
              If you leave the refund policy blank on a paid event, a default statement aligned
              with Australian Consumer Law is shown instead.
            </p>
            <div className="grid grid-cols-1 gap-5">
              {[
                { key: "event_terms", label: "Event terms", ph: "Entry conditions, age restrictions, bag policy, prohibited items…", tid: "event-terms-input" },
                { key: "refund_policy", label: "Refund policy", ph: "e.g. Full refund up to 7 days before the event. Change-of-mind refunds are not offered…", tid: "refund-policy-input" },
                { key: "event_disclaimer", label: "Disclaimer", ph: "e.g. Strobe lighting and loud music will be used. Attend at your own risk where permitted by law…", tid: "event-disclaimer-input" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="gt-form-label">
                    {f.label} <span className="text-[var(--gt-ink-3)] font-normal">(optional)</span>
                  </label>
                  <textarea
                    className="gt-input min-h-[90px]"
                    value={form[f.key]}
                    onChange={upd(f.key)}
                    placeholder={f.ph}
                    maxLength={10000}
                    data-testid={f.tid}
                  />
                  <div className="text-right text-[10px] text-[var(--gt-ink-3)] font-mono mt-1">
                    {form[f.key].length.toLocaleString()}/10,000
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 7: Add-ons / Extras (Sprint T1) — edit mode only */}
          {isEdit && (
            <div className="gt-card" data-testid="event-addons-section">
              <div className="gt-label mb-4">07 · Add-ons (Extras)</div>
              <AddonsManager eventId={id} />
            </div>
          )}



          {error && (
            <div className="text-sm text-[var(--gt-accent)] font-medium" data-testid="event-form-error">
              {error}
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-end gap-3 pt-4 sticky bottom-0 bg-white/95 backdrop-blur border-t border-[var(--gt-line)] py-4 -mx-6 sm:-mx-10 px-6 sm:px-10">
            {!isEdit && (
              <>
                <button
                  type="button"
                  onClick={() => onCreate(false)}
                  className="gt-btn-secondary"
                  disabled={saving || publishing}
                  data-testid="save-draft-btn"
                >
                  {saving && !publishing && <Loader2 size={14} className="animate-spin" />}
                  Save as draft
                </button>
                <button
                  type="button"
                  onClick={() => onCreate(true)}
                  className="gt-btn-primary"
                  disabled={saving || publishing}
                  data-testid="save-and-publish-btn"
                >
                  {(saving || publishing) && <Loader2 size={14} className="animate-spin" />}
                  Save & publish
                </button>
              </>
            )}
            {isEdit && (
              <button
                type="button"
                onClick={onUpdate}
                className="gt-btn-primary"
                disabled={saving}
                data-testid="save-changes-btn"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Save changes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Admin all-organiser edit — override confirmation dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={(o) => !o && !saving && setOverrideDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-override-dialog">
          <DialogHeader>
            <DialogTitle>Confirm admin override</DialogTitle>
            <DialogDescription>
              This event already has ticket sales. The following changes normally require organiser
              consent and are audit-logged with your admin identity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="gt-label mb-2">Changes to persist</div>
              <ul className="list-disc pl-5 text-sm text-[var(--gt-ink)] space-y-1" data-testid="admin-override-changes-list">
                {(overrideDialog?.materialChanges || []).map((c) => (
                  <li key={c.field}>{c.label}</li>
                ))}
              </ul>
            </div>

            <div>
              <label className="gt-form-label">
                Reason <span className="text-[var(--gt-accent)]">*</span>
              </label>
              <textarea
                className="gt-input min-h-[80px]"
                value={overrideReason}
                maxLength={500}
                onChange={(ev) => setOverrideReason(ev.target.value)}
                placeholder="e.g. Organiser requested venue change due to flooding at original site."
                data-testid="admin-override-reason-input"
              />
              <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">
                Stored on the audit log against your admin account.
              </p>
            </div>

            {overrideDialog?.notifyDefault && (
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="admin-override-notify-label">
                <input
                  type="checkbox"
                  checked={notifyBuyers}
                  onChange={(ev) => setNotifyBuyers(ev.target.checked)}
                  className="mt-1"
                  data-testid="admin-override-notify-checkbox"
                />
                <span>
                  Email affected buyers about this change (recommended).
                  <span className="block text-[11px] text-[var(--gt-ink-3)]">
                    Sends a heads-up with previous vs updated details. Existing tickets stay valid.
                  </span>
                </span>
              </label>
            )}

            <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="admin-override-confirm-label">
              <input
                type="checkbox"
                checked={overrideConfirmed}
                onChange={(ev) => setOverrideConfirmed(ev.target.checked)}
                className="mt-1"
                data-testid="admin-override-confirm-checkbox"
              />
              <span>
                I understand that this event already has ticket sales and confirm this administrative
                override.
              </span>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="gt-btn-ghost text-sm"
                onClick={() => setOverrideDialog(null)}
                disabled={saving}
                data-testid="admin-override-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                className="gt-btn-primary text-sm"
                onClick={confirmOverrideAndSave}
                disabled={saving || !overrideConfirmed || !overrideReason.trim()}
                data-testid="admin-override-save-btn"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Save with override
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
